import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

// [columns, rows] per level — must be even product.
const LAYOUTS: [number, number][] = [
  [3, 2], // 6 cards / 3 pairs
  [4, 3], // 12 / 6
  [4, 4], // 16 / 8
  [5, 4], // 20 / 10
  [6, 5], // 30 / 15
];

const ICONS = ['🍎', '🚀', '🎸', '🐶', '🌵', '⚽', '🎩', '🍕', '🦋', '⭐', '🎈', '🍩', '🧩', '🔔', '🌈'];

interface Card {
  id: number;
  icon: string;
  flipped: boolean;
  matched: boolean;
}

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDeck(cols: number, rows: number): Card[] {
  const pairs = (cols * rows) / 2;
  const chosen = shuffle(ICONS).slice(0, pairs);
  const deck = shuffle([...chosen, ...chosen]).map((icon, i) => ({
    id: i, icon, flipped: false, matched: false,
  }));
  return deck;
}

const GameMemory: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('memory');
  const [level, setLvl] = useState(prog.level || 1);
  const [cols, rows] = LAYOUTS[Math.min(level - 1, LAYOUTS.length - 1)];
  const [deck, setDeck] = useState<Card[]>(() => buildDeck(cols, rows));
  const [picks, setPicks] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [done, setDone] = useState(false);
  const [best] = useState(getHighScore('memory'));
  const [score, setScore] = useState(0);
  const lockRef = useRef(false);
  const savedRef = useRef(false);

  const totalPairs = (cols * rows) / 2;

  const reset = useCallback((lvl: number) => {
    const [c, r] = LAYOUTS[Math.min(lvl - 1, LAYOUTS.length - 1)];
    setDeck(buildDeck(c, r));
    setPicks([]);
    setMoves(0);
    setMatches(0);
    setSeconds(0);
    setDone(false);
    setScore(0);
    savedRef.current = false;
    lockRef.current = false;
    setLvl(lvl);
    setLevel('memory', lvl);
  }, []);

  // Timer runs until solved.
  useEffect(() => {
    if (done) return;
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, [done]);

  const flip = (idx: number) => {
    if (lockRef.current || done) return;
    const card = deck[idx];
    if (card.flipped || card.matched) return;
    const newPicks = [...picks, idx];
    setDeck(d => d.map((c, i) => (i === idx ? { ...c, flipped: true } : c)));
    setPicks(newPicks);

    sfx.move();
    if (newPicks.length === 2) {
      setMoves(m => m + 1);
      lockRef.current = true;
      const [a, b] = newPicks;
      if (deck[a].icon === deck[b].icon) {
        sfx.pop();
        setTimeout(() => {
          setDeck(d => d.map((c, i) => (i === a || i === b ? { ...c, matched: true } : c)));
          setPicks([]);
          setMatches(m => {
            const nm = m + 1;
            if (nm === totalPairs) setDone(true);
            return nm;
          });
          lockRef.current = false;
        }, 350);
      } else {
        setTimeout(() => {
          setDeck(d => d.map((c, i) => (i === a || i === b ? { ...c, flipped: false } : c)));
          setPicks([]);
          lockRef.current = false;
        }, 800);
      }
    }
  };

  // Score on completion: reward speed + few moves + level size.
  useEffect(() => {
    if (done && !savedRef.current) {
      savedRef.current = true;
      const par = totalPairs * 2; // a perfect game = totalPairs moves; par is lenient
      const movePenalty = Math.max(0, moves - totalPairs) * 8;
      const timePenalty = seconds * 2;
      const base = totalPairs * 120 * level;
      const final = Math.max(50, base - movePenalty - timePenalty);
      setScore(final);
      recordScore('memory', final, level);
      unlockNext('memory', level);
      sfx.win();
      void par;
    }
  }, [done, moves, seconds, totalPairs, level]);

  return (
    <div className="gmemory">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{moves}</b><i>moves</i></span>
          <span className="gstat"><b>{matches}/{totalPairs}</b><i>pairs</i></span>
          <span className="gstat"><b>{seconds}s</b><i>time</i></span>
          <span className="gstat"><b>{best}</b><i>best</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {LAYOUTS.map((_, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('memory').unlockedLevel;
          return (
            <button key={lvl} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)} disabled={!unlocked}>
              {unlocked ? `Lvl ${lvl}` : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="gmemory-board" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, maxWidth: cols * 84 }}>
        {deck.map((c, i) => (
          <button
            key={c.id}
            className={`gmem-card ${c.flipped || c.matched ? 'flipped' : ''} ${c.matched ? 'matched' : ''}`}
            onClick={() => flip(i)}
            aria-label={c.flipped || c.matched ? c.icon : 'hidden card'}
          >
            <span className="gmem-face gmem-back">?</span>
            <span className="gmem-face gmem-front">{c.icon}</span>
          </button>
        ))}
      </div>

      {done && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">🎉</div>
            <h3>Level {level} cleared!</h3>
            <p>{moves} moves · {seconds}s · <b>{score}</b> points{level < LAYOUTS.length ? ' · next level unlocked' : ''}</p>
            <div className="go-actions">
              {level < LAYOUTS.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">Flip two cards — match all the pairs. Fewer moves &amp; less time = more points.</p>
    </div>
  );
};

export default GameMemory;
